// Shared authenticated file download with progress reporting.
//
// Replaces the four near-identical `fetch → await response.blob() → object-URL →
// synthetic <a download> click` copies that used to live in Download.jsx,
// MasterPortal.jsx, Firmware.jsx, and SupportPortal/api.js. The old blob()
// approach buffered the whole file with no progress events; this streams the
// body and counts bytes against Content-Length so callers can show a percentage.
//
// Requires the backend to send Content-Length and expose it via CORS
// (Access-Control-Expose-Headers). When the size is unknown, onProgress is
// called once with null so the caller can show an indeterminate bar instead.

// Trigger a browser "save file" for a blob using the same object-URL + anchor
// pattern the old code used, including the delayed revoke.
function saveBlob(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = (filename || 'download').split('/').pop();
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
}

/**
 * Download a file with progress.
 *
 * @param {string} url - full request URL
 * @param {string} filename - suggested save-as name (path prefix is stripped)
 * @param {object} [opts]
 * @param {object} [opts.headers] - extra request headers (e.g. Authorization)
 * @param {(percent: number|null) => void} [opts.onProgress] - progress callback;
 *        receives 0..100 when the size is known, or null (once) when it is not.
 * @returns {Promise<{ ok: boolean, status: number }>} - lets callers keep their
 *        existing 401/403/etc. handling. Only saves the file when ok is true.
 */
export async function downloadWithProgress(url, filename, { headers = {}, onProgress } = {}) {
  const response = await fetch(url, { headers });

  // Let the caller handle non-OK (401/403/404/…) exactly as before.
  if (!response.ok) {
    return { ok: false, status: response.status };
  }

  const totalHeader = response.headers.get('Content-Length');
  const total = totalHeader ? parseInt(totalHeader, 10) : 0;

  // Fallback when streaming isn't available (very old browsers / no body):
  // no progress, but the download still works.
  if (!response.body || typeof response.body.getReader !== 'function') {
    if (onProgress) onProgress(null);
    const blob = await response.blob();
    saveBlob(blob, filename);
    return { ok: true, status: response.status };
  }

  const reader = response.body.getReader();
  const chunks = [];
  let received = 0;
  let sentIndeterminate = false;

  // Kick off at 0% when we know the size, so the bar appears immediately.
  if (total > 0 && onProgress) onProgress(0);

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;

    if (onProgress) {
      if (total > 0) {
        onProgress(Math.min(100, Math.round((received / total) * 100)));
      } else if (!sentIndeterminate) {
        onProgress(null); // size unknown — show an indeterminate bar
        sentIndeterminate = true;
      }
    }
  }

  const blob = new Blob(chunks);
  if (total > 0 && onProgress) onProgress(100);
  saveBlob(blob, filename);
  return { ok: true, status: response.status };
}
